import Link from "next/link"
import React from "react"

import Spinner from "@modules/common/icons/spinner"

type ButtonVariant = "primary" | "inverted" | "hero" | "disabled"

type ButtonProps = {
  children?: React.ReactNode
  onClick?: () => void
  variant?: ButtonVariant
  disabled?: boolean
  className?: string
  loading?: boolean
}

type LinkButtonProps = {
  variant?: ButtonVariant
  children?: React.ReactNode
  href: string
  className?: string
}

const baseClasses = "py-4 px-8 rounded-full text-lg transition-colors transition-opacity duration-300 border-none text-center"

const getVariantClasses = (variant: ButtonVariant) => {
  switch (variant) {
    case "inverted":
      return "font-nunito font-medium bg-white text-nelo-blue hover:bg-nelo-citron focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
    case "hero":
      return "font-bold font-manrope bg-white text-nelo-blue hover:bg-nelo-citron focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
    case "disabled":
      return "font-nunito font-medium bg-[#AAAAAA] text-white focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
    default:
      return "font-nunito font-medium bg-nelo-blue text-white hover:bg-nelo-blue-hover focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
  }
}

const LoadingIndicator = ({ visible }: { visible: boolean }) => (
  <div className={`inline-block transition-all duration-300 ${visible ? "mr-2 w-4 opacity-100" : "mr-0 w-0 opacity-0"}`}>
    <Spinner />
  </div>
)

export const Button = ({
  children,
  onClick,
  variant = "primary",
  disabled = false,
  className = "",
  loading = false,
  ...props
}: ButtonProps) => {
  return (
    <button
      className={`${baseClasses} ${getVariantClasses(variant)} ${className} flex items-center justify-center`}
      style={{ WebkitTapHighlightColor: "transparent" }}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      <LoadingIndicator visible={loading} />
      {children}
    </button>
  )
}

export const LinkButton = ({ variant = "primary", children, href, className = "", ...props }: LinkButtonProps) => {
  return (
    <Link href={href} className={`${baseClasses} ${getVariantClasses(variant)} ${className}`} {...props}>
      {children}
    </Link>
  )
}

export default Button
